import { FC, forwardRef, ReactNode, ReactElement, useState, cloneElement, useImperativeHandle } from 'react';
import cn from 'classnames';
import { Modal, ModalProps, DrawerProps } from 'antd';
import styles from './index.module.less';
import Iconfont from '@base/lib/Iconfont';
import { IThemeDrawerProps, ThemeDrawer } from '@base/lib/ThemeDrawer';
import { useTrans } from 'base/utils/i18n';
import { Button, ButtonProps } from '@base/lib/Button';

type IHelpPopupProps = ModalProps & {
    trigger: ReactElement | ((props: { open: () => void }) => ReactElement);
    content: ReactNode | ((props: { close: () => void }) => ReactNode);
    contentSize?: 'normal' | 'large';
    drawerProps?: Omit<DrawerProps, 'onClose'> | IThemeDrawerProps;
    btnProps?: ButtonProps;
}

const isPhone = process.env.NEXT_PUBLIC_TERMINAL === 'm';

export type IModalRef = {
    open: () => void;
    close: () => void;
}

export const HelpPopup = forwardRef<IModalRef, IHelpPopupProps>((props, ref) => {
    const {
        className,
        trigger,
        title,
        content,
        contentSize = 'normal',
        drawerProps = {},
        btnProps,
        width,
        ...rest
    } = props;

    const [visible, setVisible] = useState<boolean>(false);
    const { t } = useTrans();

    const modalWidth = isPhone
        ? '85%'
        : (props.width || (contentSize === 'normal' ? 370 : 750));

    const { height, ...restDrawProps } = drawerProps;

    const open = () => setVisible(true);
    const close = () => setVisible(false);

    useImperativeHandle(ref, () => ({
        open,
        close,
    }))

    const popupContent = typeof content === 'function'
        ? content({ close })
        : content;

    return (
        <>
            {typeof trigger === 'function'
                ? trigger({ open })
                : cloneElement(trigger, {
                    onClick: open
                })
            }

            {contentSize === 'large' && isPhone ? (
                <ThemeDrawer
                    height={height || '80%'}
                    className={(cn(styles['drawer'], styles[`modal-${contentSize}`], className))}
                    title={title}
                    placement={'bottom'}
                    destroyOnClose={false}
                    footer={false}
                    open={visible}
                    onClose={close}
                    {...restDrawProps}
                >
                    {popupContent}
                </ThemeDrawer>
            ) : (
                <Modal
                    width={modalWidth}
                    open={visible}
                    centered={true}
                    onCancel={close}
                    closable={!isPhone}
                    closeIcon={<Iconfont className={'icon-x'}/>}
                    className={(cn(styles['modal'], styles[`modal-${contentSize}`], className))}
                    title={title}
                    footer={
                        <Button
                            className={'g-footer-btn'}
                            type={'primary'}
                            onClick={close}
                        >
                            {t('web_common_btn_ok')}
                        </Button>
                    }
                    {...rest}
                >
                    {popupContent}
                </Modal>
            )}
        </>
    )
});

export default HelpPopup;